import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAddAnnotationCommentMutation } from "api/virtual-tour/mutations/useAddAnnotationCommentMutation";
import { useEditAnnotationCommentMutation } from "api/virtual-tour/mutations/useEditAnnotationCommentMutation";
import CONFIG from "utils/config";
import CommentEditor from "components/CommentEditor/CommentEditor";
import { useCallback, useEffect, useRef, useState } from "react";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import { useWorkspaces } from "contexts/WorkspacesContext";
import { useGetWorkspaceUsersQuery } from "api/workspace/queries/useGetWorkspaceUsersQuery";
import { useCreateAnnotationMutation } from "api/virtual-tour/mutations/useCreateAnnotationMutation";
import { useAtom } from "jotai";
import { newAnnotationAtom } from "store/atoms/newAnnotation";
import { vtourAnnotationsLengthAtom } from "store/atoms/vtourAnnotationsLengthAtom";
import { fillInMissingUserInWorkspaceUsers, WorkspaceUserWithPossibleMissingValues } from "../../types/workspaceUser";

type NewCommentProps = {
  annotation: VirtualTourAnnotation;
  type?: 'annotation' | 'comment';
  onClose?: () => void;
  tourId?: string;
};

export const NewComment = ({ annotation, type = 'comment', onClose, tourId }: NewCommentProps) => {
  const { mutate: addComment, isPending: isAddPending } = useAddAnnotationCommentMutation();
  const { mutate: createAnnotation, isPending: isCreatePending } = useCreateAnnotationMutation();
  const { mutate: editAnnotationComment, isPending: isEditPending } = useEditAnnotationCommentMutation();
  const [newAnnotation, setNewAnnotation] = useAtom(newAnnotationAtom);
  const [vtourAnnotationsLength, setVtourAnnotationsLength] = useAtom(vtourAnnotationsLengthAtom);
  const { scanId = "" } = useParams();
  const uppyRef = useRef<Uppy | null>(null);
  const currentCommentIdRef = useRef<string>("");
  const [uploading, setUploading] = useState<string>("");

  if (type === 'annotation' && !newAnnotation) return null;

  // Get workspace users
  const { activeWorkspace: { id: workspaceId } } = useWorkspaces();
  const { data } = useGetWorkspaceUsersQuery(workspaceId);

  const workspaceUsers = fillInMissingUserInWorkspaceUsers((data || []) as WorkspaceUserWithPossibleMissingValues[]);

  const getCommentAttachmentS3Path = (type: 'annotation' | 'comment', id: string, fileName: string) => {
    let path = "";
    switch (type) {
      case 'annotation':
        path = `${scanId}/annotations/${id}/${fileName}`;
        break;
      case 'comment':
        path = `${scanId}/comments/${id}/${fileName}`;
        break;
    }
    return path;
  };

  const createUppyInstance = useCallback(() => {
    const uppyInstance = new Uppy({
      restrictions: {
        maxNumberOfFiles: 5,
        maxFileSize: 10 * 1024 * 1024,
        allowedFileTypes: ['image/*']
      },
      autoProceed: false,
      onBeforeFileAdded: (currentFile) => {
        const currentFileNameParts = currentFile.name?.split(".") || [];
        currentFileNameParts[0] = currentFileNameParts[0].concat(`_${new Date().getTime()}`);
        return {
          ...currentFile,
          name: currentFileNameParts.join(".").replace(/\s+/g, "_"),
        };
      },
    }).use(AwsS3, {
      shouldUseMultipart: false,
      getUploadParameters: async (file) => {
        const response = await fetch(`${CONFIG.API_URL}/v2/virtual-tour/annotation/attachment/uploadurl`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            s3Key: file.meta.s3Key,
            type: file.meta.type
          })
        });
        if (!response.ok) {
          throw new Error('Failed to get upload URL');
        }

        return await response.json();
      },
    });

    uppyInstance.on("complete", (result) => {
      setUploading("");
      if (type === 'annotation') {
        setNewAnnotation(null);
        toast("Annotation added successfully!", {
          type: "success",
        });
        setVtourAnnotationsLength(prev => prev + 1);
        onClose?.();
      }
    });

    uppyInstance.on("error", (error) => {
      console.error('Upload failed:', error);
      toast.error("Failed to upload attachments");
      setUploading("");
    });

    uppyInstance.on("restriction-failed", (file, error) => {
      let message = "File upload failed: ";
      if (error.message.includes("maxNumberOfFiles")) {
        message += "Maximum 5 files allowed per comment";
      } else if (error.message.includes("maxFileSize")) {
        message += "Maximum file size is 10MB";
      } else {
        message += error.message;
      }
      toast.error(message);
    });

    return uppyInstance;
  }, [editAnnotationComment, setUploading]);

  useEffect(() => {
    uppyRef.current = createUppyInstance();
    return () => {
      if (uppyRef.current) uppyRef.current.destroy();
    };
  }, [createUppyInstance]);

  const handleSubmit = async (formattedText: string, mentions: string[], cleanMessage: string, files?: File[]) => {

    if (formattedText === "" || cleanMessage === "") {
      toast.error("Comment cannot be empty");
      return;
    }

    if (!files || files.length === 0) {

      if (type === 'comment') {
        addComment(
          {
            annotationId: annotation.id,
            type: "TEXT",
            message: formattedText,
            cleanMessage,
            version: 2,
            mentions
          },
          {
            onSuccess: () => {
              toast.success("Comment added successfully");
            },
          }
        );
      } else if (type === 'annotation') {
        if (!tourId || !newAnnotation) return;
        createAnnotation(
          {
            position: newAnnotation.position,
            tourId,
            type: "TEXT",
            message: formattedText,
            cleanMessage: cleanMessage,
            mentions,
          },
          {
            onSuccess: () => {
              setNewAnnotation(null);
              toast("Annotation added successfully!", {
                type: "success",
              });
              setVtourAnnotationsLength(prev => prev + 1);
              onClose?.();
            },
            onError: (err) => {
              console.error("Failed to create annotation", err);
              toast("Failed to create annotation", {
                type: "error",
              });
            },
          }
        );
      }
      return;
    }

    if (files.length > 0) {
      const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        toast.error("Some files exceed the 10MB size limit");
        return;
      }
      if (files.length > 5) {
        toast.error("Maximum 5 files allowed per comment");
        return;
      }

      if (type === 'comment') {
        addComment(
          {
            annotationId: annotation.id,
            type: "TEXT",
            message: formattedText,
            cleanMessage,
            version: 2,
            mentions,
            metadata: {
              attachments: files?.map(file => ({
                filename: file.name || ""
              }))
            }
          },
          {
            onSuccess: ({ id }) => {
              setUploading(id);
              currentCommentIdRef.current = id;

              files.forEach(file => {
                uppyRef.current?.addFile({
                  name: file.name,
                  type: file.type,
                  data: file,
                  meta: {
                    s3Key: getCommentAttachmentS3Path(type, id, file.name),
                    type: file.type,
                  }
                });
              });

              uppyRef.current?.upload();
            },
          }
        );
      } else if (type === 'annotation') {
        if (!tourId || !newAnnotation) return;
        createAnnotation(
          {
            position: newAnnotation.position,
            tourId,
            type: "TEXT",
            metadata: {
              attachments: files?.map(file => ({
                filename: file.name || ""
              })) || [],
            },
            message: formattedText,
            cleanMessage: cleanMessage,
            mentions,
          },
          {
            onSuccess: ({ id }) => {
              setUploading(id);
              currentCommentIdRef.current = id;

              files.forEach(file => {
                uppyRef.current?.addFile({
                  name: file.name,
                  type: file.type,
                  data: file,
                  meta: {
                    s3Key: getCommentAttachmentS3Path(type, id, file.name),
                    type: file.type,
                  }
                });
              });

              uppyRef.current?.upload();
            },
            onError: (err) => {
              console.error("Failed to create annotation", err);
              toast("Failed to create annotation", {
                type: "error",
              });
            },
          }
        );
      }
    }
  };

  return (
    // <Stack direction="column" padding={0.5}>
    <CommentEditor onSubmit={handleSubmit} users={workspaceUsers || []} />
    // </Stack>
  );
};
